import React, { useState, useEffect, useContext } from 'react';
import { useQuery } from '@apollo/client';
import { injectIntl, FormattedMessage } from 'react-intl';
import { SVGIcon } from '@superops/super-icons';
import { isEmpty } from 'lodash';

import {
  DromoUploader,
  getTranslatedText,
  AppContext,
  useCheckFeatureGating
} from '@nile/components';
import Loader from '@nile/mangifera/src/Components/Ticket/TicketCount';

import {
  getServiceCatalogImportFields,
  getNewServiceCatalogImportFields,
  shapeOptions,
  getQuery,
  callQuery
} from '../Fields/serviceItemFields';
import { intlConverter } from '../utils/helpers';
import {
  AccountClient,
  GET_SERVICE_CATALOG_ITEMS_FOR_TABLE
} from '@nile/graphql';

const ServiceCatalogUploader = ({
  onResults,
  onCancel,
  validationProps = {},
  fields: propFields,
  userToken,
  intl,
  activator
}) => {
  const appContext = useContext(AppContext);
  const { currentProduct } = appContext;
  const contractsNew = useCheckFeatureGating('CONTRACT_V3');
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);
  const [fields, setFields] = useState([]);

  const [availableServiceItems, setAvailableServiceItems] = useState([]);

  const { loading: serviceCatalogItemsLoading } = useQuery(
    GET_SERVICE_CATALOG_ITEMS_FOR_TABLE,
    {
      fetchPolicy: 'no-cache',
      client: AccountClient,
      variables: {
        listInfo: {
          sort: [
            {
              attribute: 'name',
              order: 'ASC'
            }
          ],
          condition: {
            joinOperator: 'AND',
            operands: [
              {
                attribute: 'status',
                operator: 'is',
                value: 'ACTIVE'
              }
            ]
          }
        }
      },
      onCompleted: (res) => {
        if (res?.getServiceCatalogItems?.items) {
          setAvailableServiceItems(res?.getServiceCatalogItems?.items);
        }
      }
    }
  );

  function getEligibleServiceItems(offeringType) {
    let conditionToCheck = (item) => true;
    switch (offeringType) {
      case 'BLOCK_MONEY':
        conditionToCheck = (item) => !!item?.offerAsWorklogItem;
        break;
      case 'BLOCK_HOUR':
        conditionToCheck = (item) =>
          item?.serviceTypeItem?.offeringType === 'HOURS';
        break;
      case 'SERVICE_BUNDLE':
        conditionToCheck = (item) =>
          item?.serviceTypeItem?.offeringType !== 'SERVICE_BUNDLE';
        break;
    }
    return availableServiceItems?.filter(conditionToCheck)?.map((item) => ({
      label: item?.name,
      value: item?.itemId
    }));
  }

  const handleRowhooksNew = ({ row, index }) => {
    const newRecord = { row, index };
    const validationFieldsArray = [
      'taxId',
      'quantity',
      'unitPrice',
      'bundledItems',
      'offeringType',
      'applicableType',
      'applicableHours'
    ];
    validationFieldsArray.forEach((key) => {
      newRecord.row[key].info = [];
    });

    function addError(key, errorKey) {
      newRecord.row[key].info = [
        {
          message: getTranslatedText(
            intl,
            errorKey || 'dromo.validation.requiredField'
          ),
          level: 'error'
        }
      ];
    }

    function addSelectOptions(key, options) {
      newRecord.row[key].selectOptions = options;
    }

    function addRequiredError(key) {
      addError(key, 'dromo.validation.requiredField');
    }

    // const elegibleServiceItems = getEligibleServiceItems(
    //   newRecord.row.offeringType.resultValue
    // );

    if (
      newRecord.row.itemType.resultValue === 'SERVICE' &&
      !newRecord.row.offeringType.resultValue
    ) {
      addRequiredError('offeringType');
    }

    if (
      newRecord.row.offeringType.resultValue === 'BLOCK_MONEY' ||
      newRecord.row.offeringType.resultValue === 'BLOCK_HOUR'
    ) {
      if (isEmpty(newRecord.row.applicableHours.resultValue)) {
        addRequiredError('applicableHours');
      }
      if (isEmpty(newRecord.row.applicableType.resultValue)) {
        addRequiredError('applicableType');
      }
      if (!newRecord.row.quantity.resultValue) {
        addRequiredError('quantity');
      }
      if (newRecord.row.offeringType.resultValue === 'BLOCK_HOUR') {
        if (!newRecord.row.unitPrice.resultValue) {
          addRequiredError('unitPrice');
        }
        newRecord.row.applicableType.selectOptions = [
          {
            label: 'Included Items',
            value: 'INCLUDED_ITEMS'
          }
        ];
      } else {
        newRecord.row.applicableType.selectOptions = [
          {
            label: 'All Items',
            value: 'ALL_ITEMS'
          },
          {
            label: 'Included Items',
            value: 'INCLUDED_ITEMS'
          }
        ];
      }
    } else {
      if (!newRecord.row.unitPrice.resultValue) {
        addRequiredError('unitPrice');
      }
    }

    if (
      newRecord.row.offeringType.resultValue === 'BLOCK_MONEY' ||
      newRecord.row.offeringType.resultValue === 'BLOCK_HOUR' ||
      newRecord.row.offeringType.resultValue === 'SERVICE_BUNDLE'
    ) {
      if (
        (newRecord.row.offeringType.resultValue === 'SERVICE_BUNDLE' ||
          newRecord.row.applicableType.resultValue === 'INCLUDED_ITEMS') &&
        !newRecord.row.bundledItems.resultValue.length
      ) {
        addRequiredError('bundledItems');
      }
      // addSelectOptions('bundledItems', elegibleServiceItems);
    }

    if (
      newRecord.row.taxable.resultValue === 'true' &&
      isEmpty(newRecord.row.taxId.resultValue)
    ) {
      addRequiredError('taxId');
    }

    if (newRecord.row.itemType.resultValue === 'PRODUCT') {
      newRecord.row.offeringType.selectOptions = [];
    } else {
      newRecord.row.offeringType.selectOptions = [
        {
          label: 'Quantity',
          value: 'QUANTITY'
        },
        {
          label: 'Hours',
          value: 'HOURS'
        },
        {
          label: 'Block hour',
          value: 'BLOCK_HOUR'
        },
        {
          label: 'Block Money',
          value: 'BLOCK_MONEY'
        },
        {
          label: 'Service Bundle',
          value: 'SERVICE_BUNDLE'
        }
      ];
    }

    return newRecord;
  };

  const handleRowhooks = ({ row, index }) => {
    const newRecord = { row, index };
    const validationFieldsArray = [
      'blockHoursQuantity',
      'blockMoneyAmount',
      'applicableIn',
      'taxId'
    ];
    validationFieldsArray.forEach((key) => {
      newRecord.row[key].info = [];
    });

    function addError(key, errorKey) {
      newRecord.row[key].info = [
        {
          message: getTranslatedText(
            intl,
            errorKey || 'dromo.validation.requiredField'
          ),
          level: 'error'
        }
      ];
    }

    function addRequiredError(key) {
      addError(key, 'dromo.validation.requiredField');
    }

    if (newRecord.row.quantityType.resultValue === 'BLOCK_HOURS') {
      if (!newRecord.row.blockHoursQuantity.resultValue) {
        addRequiredError('blockHoursQuantity');
      }
      if (newRecord.row.blockHoursQuantity.resultValue < 0) {
        addError('blockHoursQuantity', 'dromo.validation.invalid.value');
      }
    }

    if (newRecord.row.quantityType.resultValue === 'BLOCK_MONEY') {
      if (!newRecord.row.blockMoneyAmount.resultValue) {
        addRequiredError('blockMoneyAmount');
      }
      if (newRecord.row.blockMoneyAmount.resultValue < 0) {
        addError('blockMoneyAmount', 'dromo.validation.invalid.value');
      }
    }
    if (
      (newRecord.row.quantityType.resultValue === 'BLOCK_MONEY' ||
        newRecord.row.quantityType.resultValue === 'BLOCK_HOURS') &&
      isEmpty(newRecord.row.applicableIn.resultValue)
    ) {
      addRequiredError('applicableIn');
    }
    if (
      newRecord.row.taxable.resultValue === 'true' &&
      isEmpty(newRecord.row.taxId.resultValue)
    ) {
      addRequiredError('taxId');
    }

    return newRecord;
  };

  useEffect(() => {
    if (!propFields) {
      setLoading(true);
      const serviceCatalogImportFields = contractsNew
        ? getNewServiceCatalogImportFields(intl, currentProduct)
        : getServiceCatalogImportFields(intl, currentProduct);
      const initFields = [...serviceCatalogImportFields];

      const selectFields = serviceCatalogImportFields.reduce(
        (acc, field, idx) => {
          if (field.type === 'select')
            acc[idx] = getQuery(field.key)
              ? callQuery(field.key)
              : field.selectOptions;
          return acc;
        },
        {}
      );

      const promises = Object.values(selectFields);
      Promise.all(promises)
        .then((options) => {
          options.forEach((set, idx) => {
            const indexToModify = Object.keys(selectFields)[idx];
            initFields[indexToModify].selectOptions = shapeOptions(
              initFields[indexToModify].key,
              set
            );
          });
          setFields([...intlConverter(intl, initFields)]);
          setLoading(false);
        })
        .catch((e) => {
          setLoading(false);
          setError(true);
          console.log(e);
        });
    }
  }, []);

  const handleReviewStep = (instance, data) => {
    const availableFields = Object.keys(data?.fields);

    const serviceCatalogImportFields = contractsNew
      ? getNewServiceCatalogImportFields(intl, currentProduct)
      : getServiceCatalogImportFields(intl, currentProduct);

    const fieldsToAdd = serviceCatalogImportFields?.filter(
      (field) =>
        !('validators' in field) && !availableFields.includes(field.key)
    );
    if (fieldsToAdd.length) {
      const fieldsToAddIntl = intlConverter(intl, fieldsToAdd);
      fieldsToAddIntl.forEach((field) => {
        instance.addField({ ...field, type: field?.type || 'string' });
      });
    }
  };

  if (loading) return <Loader loading />;

  if (error) return null;

  return (
    <DromoUploader
      settings={{
        importIdentifier: contractsNew
          ? 'SERVICE_CATALOG_ITEM'
          : 'SERVICE_ITEM',
        title: getTranslatedText(intl, 'dromo.serviceItem.import.title')
      }}
      fields={propFields || fields}
      rowHooks={[
        (row) => row,
        contractsNew ? handleRowhooksNew : handleRowhooks
      ]}
      columnHooks={[(column) => column]}
      stepHooks={[{ type: 'reviewStep', callback: handleReviewStep }]}
      onResults={(response, metadata) => {
        setResults(response);
        if (onResults) onResults();
      }}
      onCancel={() => {
        if (onCancel) onCancel();
      }}
      activator={
        activator || (
          <div className="flex center actionListItemPadding ptr">
            <div className="mr-10 flex center">
              <SVGIcon name="Shopping" />
            </div>
            <FormattedMessage id="dromo.csv.requester.import" />
          </div>
        )
      }
      userToken={userToken}
      backendSync
    />
  );
};

export default injectIntl(ServiceCatalogUploader);
